import React from 'react';
import { useExportCartStore, ExportCartStudent } from './useExportCartStore';

interface ExportCartPanelProps {
  open: boolean;
  onClose: () => void;
  /** Closes the panel and opens the export dialog straight into the "Marked for Export" scope. */
  onExportNow: () => void;
}

const PANEL_CSS = `
  @keyframes ecp-slideIn { from{opacity:0;transform:translateX(24px)} to{opacity:1;transform:translateX(0)} }
  @keyframes ecp-fadeIn  { from{opacity:0} to{opacity:1} }

  .ecp-panel {
    position: fixed; top: 0; right: 0; bottom: 0;
    width: 800px;
    max-width: 96vw;
    background: #fff;
    box-shadow: -8px 0 48px rgba(30,58,138,0.13);
    display: flex; flex-direction: column;
    z-index: 1000;
    animation: ecp-slideIn 0.28s cubic-bezier(0.4,0,0.2,1);
    font-family: 'DM Sans', system-ui, sans-serif;
  }
  .ecp-scroll::-webkit-scrollbar { width: 4px; }
  .ecp-scroll::-webkit-scrollbar-track { background: transparent; }
  .ecp-scroll::-webkit-scrollbar-thumb { background: rgba(30,58,138,0.12); border-radius: 4px; }

  .ecp-close-btn {
    width: 32px; height: 32px; border-radius: 9px;
    background: rgba(255,255,255,0.15);
    border: 1px solid rgba(255,255,255,0.25);
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    padding: 0; transition: background 0.15s; flex-shrink: 0;
  }
  .ecp-close-btn:hover { background: rgba(255,255,255,0.28); }

  .ecp-group {
    margin-bottom: 18px;
  }
  .ecp-group-header {
    display: flex; align-items: center; gap: 8px;
    margin-bottom: 8px; padding: 0 2px;
  }
  .ecp-group-title {
    font-size: 12.5px; font-weight: 800; color: #1e3a8a;
    text-transform: uppercase; letter-spacing: 0.04em;
    white-space: nowrap;
  }
  .ecp-group-count {
    font-size: 10.5px; font-weight: 700; color: #64748b;
    background: #f1f5f9; border: 1px solid #e2e8f0;
    padding: 1px 8px; border-radius: 99px;
    white-space: nowrap;
  }
  .ecp-group-line {
    flex: 1; height: 1px; background: #e5e7eb;
  }
  .ecp-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  @media (max-width: 640px) {
    .ecp-grid { grid-template-columns: 1fr; }
  }

  .ecp-card {
    border-radius: 12px;
    border: 1px solid #f0f4f8;
    background: white;
    padding: 12px;
    animation: ecp-fadeIn 0.18s ease both;
    transition: box-shadow 0.15s, transform 0.13s;
    min-width: 0;
  }
  .ecp-card:hover { box-shadow: 0 3px 16px rgba(30,58,138,0.09); transform: translateY(-1px); }

  .ecp-remove-btn {
    width: 26px; height: 26px; border-radius: 8px;
    border: 1px solid #fecaca; background: rgba(239,68,68,0.06);
    color: #dc2626; cursor: pointer; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.15s;
  }
  .ecp-remove-btn:hover { background: rgba(239,68,68,0.14); }

  .ecp-toolbar-btn {
    padding: 7px 14px; border-radius: 8px; border: 1px solid #e5e7eb;
    background: white; font-size: 12px; font-weight: 600; color: #374151;
    cursor: pointer; font-family: inherit; transition: all 0.15s;
  }
  .ecp-toolbar-btn:hover { background: #f9fafb; border-color: #d1d5db; }
  .ecp-toolbar-btn.primary {
    background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
    color: white; border: none;
  }
  .ecp-toolbar-btn.primary:hover { box-shadow: 0 4px 12px rgba(30,58,138,0.25); transform: translateY(-1px); }
  .ecp-toolbar-btn.danger { color: #dc2626; border-color: #fecaca; background: rgba(239,68,68,0.04); }
  .ecp-toolbar-btn.danger:hover { background: rgba(239,68,68,0.1); }
  .ecp-toolbar-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none !important; box-shadow: none !important; }
`;

const Avatar: React.FC<{ name: string; pictureUrl?: string | null; size?: number }> = ({
  name, pictureUrl, size = 44,
}) => {
  const [err, setErr] = React.useState(false);
  if (pictureUrl && !err) {
    return (
      <img
        src={pictureUrl}
        alt={name}
        onError={() => setErr(true)}
        style={{
          width: size, height: size, borderRadius: '50%',
          objectFit: 'cover', flexShrink: 0,
          border: '2px solid #e5e7eb',
        }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg,#dbeafe,#bfdbfe)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 700, color: '#1e40af',
      border: '2px solid #93c5fd',
    }}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
};

const StudentCard: React.FC<{ student: ExportCartStudent; onRemove: () => void }> = ({ student, onRemove }) => (
  <div className="ecp-card">
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <Avatar name={student.name} pictureUrl={student.profile_picture_url} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {student.name}
          </div>
          <button className="ecp-remove-btn" onClick={onRemove} title="Remove from export cart">
            <i className="pi pi-times" style={{ fontSize: 11 }} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 4, marginBottom: 8 }}>
          <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: '#eff6ff', color: '#1e40af', fontWeight: 600, border: '1px solid #bfdbfe' }}>
            Roll {student.roll_no}
          </span>
          <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: '#f5f3ff', color: '#5b21b6', fontWeight: 600, border: '1px solid #ddd6fe' }}>
            {student.class_name}{student.section ? ` · ${student.section}` : ''}
          </span>
          <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: '#f8fafc', color: '#64748b', fontWeight: 600, border: '1px solid #e2e8f0' }}>
            Adm. {student.admission_no}
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {(student.father_name || student.father_no) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#374151' }}>
              <i className="pi pi-user" style={{ fontSize: 10, color: '#9ca3af', width: 12 }} />
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{student.father_name || '—'}</span>
              {student.father_no && (
                <span style={{ color: '#9ca3af', display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                  <i className="pi pi-phone" style={{ fontSize: 9 }} />
                  {student.father_no}
                </span>
              )}
            </div>
          )}
          {(student.mother_name || student.mother_no) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#374151' }}>
              <i className="pi pi-user" style={{ fontSize: 10, color: '#9ca3af', width: 12 }} />
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{student.mother_name || '—'}</span>
              {student.mother_no && (
                <span style={{ color: '#9ca3af', display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                  <i className="pi pi-phone" style={{ fontSize: 9 }} />
                  {student.mother_no}
                </span>
              )}
            </div>
          )}
          {student.address && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11.5, color: '#6b7280' }}>
              <i className="pi pi-map-marker" style={{ fontSize: 10, color: '#9ca3af', width: 12, marginTop: 2 }} />
              <span style={{ lineHeight: 1.4 }}>{student.address}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

// ─── Grouping ─────────────────────────────────────────────────────────────
// Groups by class_name + section. `section` may be null/empty — those
// students are grouped under the class alone, with no section suffix.
interface StudentGroup {
  key: string;
  className: string;
  section: string | null;
  students: ExportCartStudent[];
}

function groupStudents(students: ExportCartStudent[]): StudentGroup[] {
  const map = new Map<string, StudentGroup>();

  students.forEach((s) => {
    const className = s.class_name?.trim() || 'Unassigned';
    const section = s.section?.trim() || null;
    const key = `${className}__${section ?? ''}`;

    if (!map.has(key)) {
      map.set(key, { key, className, section, students: [] });
    }
    map.get(key)!.students.push(s);
  });

  return Array.from(map.values()).sort((a, b) => {
    const byClass = a.className.localeCompare(b.className, undefined, { numeric: true, sensitivity: 'base' });
    if (byClass !== 0) return byClass;
    return (a.section ?? '').localeCompare(b.section ?? '', undefined, { numeric: true, sensitivity: 'base' });
  });
}

const ExportCartPanel: React.FC<ExportCartPanelProps> = ({ open, onClose, onExportNow }) => {
  const itemsMap = useExportCartStore((s) => s.items);
  const remove = useExportCartStore((s) => s.remove);
  const clear = useExportCartStore((s) => s.clear);
  const students = React.useMemo(() => Object.values(itemsMap), [itemsMap]);
  const groups = React.useMemo(() => groupStudents(students), [students]);

  if (!open) return null;

  return (
    <div className="ecp-panel">
      <style>{PANEL_CSS}</style>

      {/* Header */}
      <div style={{
        flexShrink: 0, padding: '20px 20px 16px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #2855ae 55%, #3b82f6 100%)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, flexShrink: 0,
            background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <i className="pi pi-bookmark" style={{ fontSize: 17, color: 'white' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'white', lineHeight: 1.2 }}>
              Export Cart
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', fontWeight: 500, marginTop: 2 }}>
              {students.length} student{students.length !== 1 ? 's' : ''} marked
              {groups.length > 0 && <> · {groups.length} group{groups.length !== 1 ? 's' : ''}</>}
            </div>
          </div>
          <button className="ecp-close-btn" onClick={onClose}>
            <i className="pi pi-times" style={{ fontSize: 12, color: 'white' }} />
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ flexShrink: 0, padding: '12px 16px', borderBottom: '1px solid #f0f4f8', background: '#fafbfc', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="ecp-toolbar-btn primary" onClick={onExportNow} disabled={students.length === 0}>
          <i className="pi pi-download" style={{ marginRight: 5, fontSize: 12 }} />
          Export Marked ({students.length})
        </button>
        <button className="ecp-toolbar-btn danger" onClick={clear} disabled={students.length === 0}>
          <i className="pi pi-trash" style={{ marginRight: 5, fontSize: 12 }} />
          Clear All
        </button>
      </div>

      {/* List — grouped by class/section, 2 cards per row */}
      <div className="ecp-scroll" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
        {students.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, margin: '0 auto 16px', borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(65,105,225,0.1), rgba(91,124,230,0.05))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className="pi pi-bookmark" style={{ fontSize: 26, color: '#4169E1' }} />
            </div>
            <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700, color: '#1f2937' }}>
              Nothing marked yet
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: '#9ca3af', fontWeight: 500, lineHeight: 1.5 }}>
              Select students in the table and use "Add to Export Cart" — marks stick even
              as you switch class or section.
            </p>
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.key} className="ecp-group">
              <div className="ecp-group-header">
                <span className="ecp-group-title">
                  {group.className}{group.section ? ` · ${group.section}` : ''}
                </span>
                <span className="ecp-group-count">
                  {group.students.length} student{group.students.length !== 1 ? 's' : ''}
                </span>
                <div className="ecp-group-line" />
              </div>
              <div className="ecp-grid">
                {group.students.map((student) => (
                  <StudentCard key={student.id} student={student} onRemove={() => remove(student.id)} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ExportCartPanel;